import { configApi, subscriptionsApi, variantsApi } from 'api'
import { getConfigFromConfigRaw } from 'helpers/getConfigFromConfigRaw'
import { getVariantFromRawVariant } from 'helpers/getVariantFromRawVariant'
import { TAnswer } from 'models/common.model'
import { IConfig } from 'models/config.model'
import {
  IAction,
  IAppState,
  TAppActionThunk,
  TAppDispatchThunk,
} from 'models/store.model'
import { IVariant } from 'models/variant.model'
import { AnyAction } from 'redux'
import { ScreenName, SubscriptionListType } from 'root-constants'
import { ISubscription } from 'models/subscriptions.model'
import { getSubscriptionListFromRawSubscriptionList } from 'helpers/getSubscriptionListFromRawSubscriptionList'
import { PageId } from 'page-constants'

const MODULE_NAME = 'COMMON'

// actions types
export const START_FETCHING = `${MODULE_NAME}/START_FETCHING`
export const STOP_FETCHING = `${MODULE_NAME}/STOP_FETCHING`
export const SET_ERROR = `${MODULE_NAME}/SET_ERROR`
export const RESET_ERROR = `${MODULE_NAME}/RESET_ERROR`
export const GET_VARIANT = `${MODULE_NAME}/GET_VARIANT`
export const SET_VARIANT = `${MODULE_NAME}/SET_VARIANT`
export const GET_SUBSCRIPTION_LIST = `${MODULE_NAME}/GET_SUBSCRIPTION_LIST`
export const SET_SUBSCRIPTION_LIST = `${MODULE_NAME}/SET_SUBSCRIPTION_LIST`
export const GET_CONFIG = `${MODULE_NAME}/GET_CONFIG`
export const SET_CONFIG = `${MODULE_NAME}/SET_CONFIG`
export const SET_ANSWERS = `${MODULE_NAME}/SET_ANSWERS`
export const RESET_ANSWERS = `${MODULE_NAME}/RESET_ANSWERS`
export const SET_ANSWERS_FROM_BACKEND = `${MODULE_NAME}/SET_ANSWERS_FROM_BACKEND`
export const SET_OPTIMIZE_VARIANT_MAP = `${MODULE_NAME}/SET_OPTIMIZE_VARIANT_MAP`
export const SET_SCREEN_NAME = `${MODULE_NAME}/SET_SCREEN_NAME`
export const SET_LANGUAGE = `${MODULE_NAME}/SET_LANGUAGE`

// actions handlers
export function startFetching(action: string): IAction<string> {
  return {
    type: START_FETCHING,
    payload: action,
  }
}

export function stopFetching(actionToStop: string): any {
  return (dispatch: TAppDispatchThunk<string[]>, getState: () => IAppState) => {
    const runningActions = getState().common.actionList
    const fetchList = runningActions.filter(
      (action: string) => action && action !== actionToStop,
    )

    dispatch({
      type: STOP_FETCHING,
      payload: fetchList,
    })
  }
}

export function setErrorAction<T>(error: T): IAction<T> {
  return {
    type: SET_ERROR,
    payload: error,
  }
}

export function resetErrorAction(): IAction<never> {
  return {
    type: RESET_ERROR,
  }
}

export function setVariantAction(payload: IVariant): IAction<IVariant> {
  return {
    type: SET_VARIANT,
    payload,
  }
}

export function getVariantAction({
  cohort,
}: {
  cohort: string
}): TAppActionThunk<any> {
  return async (dispatch) => {
    dispatch(startFetching(GET_VARIANT))

    const response = await variantsApi.getVariant({ cohort })

    if (response.success && response.data) {
      const variant = getVariantFromRawVariant(response.data.variant)

      dispatch(setVariantAction(variant))
    }

    dispatch(stopFetching(GET_VARIANT))
  }
}

export function setSubscriptionListAction(
  payload: ISubscription[],
): IAction<ISubscription[]> {
  return {
    type: SET_SUBSCRIPTION_LIST,
    payload,
  }
}

export function getSubscriptionListAction(requestInfo: {
  cohort: string
  type: SubscriptionListType
}): TAppActionThunk<any> {
  return async (dispatch) => {
    dispatch(startFetching(GET_SUBSCRIPTION_LIST))

    const response = await subscriptionsApi.getSubscriptionList(requestInfo)

    if (response.success && response.data) {
      const subscriptionList = getSubscriptionListFromRawSubscriptionList(
        response.data.prices,
      )

      dispatch(setSubscriptionListAction(subscriptionList))
    }

    dispatch(stopFetching(GET_SUBSCRIPTION_LIST))
  }
}

export function setConfigAction(payload: IConfig): IAction<IConfig> {
  return {
    type: SET_CONFIG,
    payload,
  }
}

export function getConfigAction(): TAppActionThunk<any> {
  return async (dispatch) => {
    dispatch(startFetching(GET_VARIANT))

    const response = await configApi.getConfig()

    if (response.success && response.data) {
      const config = getConfigFromConfigRaw(response.data.config)
      dispatch(setConfigAction(config))
    }

    dispatch(stopFetching(GET_VARIANT))
  }
}

export function setAnswersAction({
  answers,
  pageId,
}: {
  answers: TAnswer
  pageId: PageId
}): AnyAction {
  return {
    type: SET_ANSWERS,
    payload: { [pageId]: answers },
  }
}

export function setAnswersFromBackendAction(answers: {
  [key: string]: string | string[]
}): AnyAction {
  return {
    type: SET_ANSWERS_FROM_BACKEND,
    payload: answers,
  }
}

export function resetAnswersAction(): AnyAction {
  return {
    type: RESET_ANSWERS,
  }
}

export function setOptimizeVariantMapAction({
  experimentId,
  abVariant,
}: {
  experimentId: string
  abVariant: string
}): IAction<Record<string, string>> {
  return {
    type: SET_OPTIMIZE_VARIANT_MAP,
    payload: { [experimentId]: abVariant },
  }
}

export function setScreenNameAction(
  screenName: ScreenName,
): IAction<ScreenName> {
  return {
    type: SET_SCREEN_NAME,
    payload: screenName,
  }
}

export function setLanguageAction(language: string): IAction<string> {
  return {
    type: SET_LANGUAGE,
    payload: language,
  }
}
